import axios from 'axios';
import ApiHelper from '@/helpers/api-helper';

export default class BudgetService {

	public static BUDGET_TYPE = 1;
	public static BUDGET_PACKAGE_TYPE = 2;

	private static fields = [
		'students',
		'teachers',
		'start_date',
		'end_date',
		'who',
		'responsible_name_surname',
		'center',
		'responsible_email',
		'responsible_phone',
		'others',
		'budget_id',
		'customer_state_id',
		'customer_id',
		'ages_id',
		'travel_id',
		'customer_budget_id',
		'accommodation',
		'transport',
		'extra_service',
		'activities',
		'type',
		'terms',
		'privacity',
	];

    private static pdf_fields = [
        'admin_total_amount',
        'admin_notes',
        'admin_accommodation_notes',
        'admin_transport_notes',
        'admin_extra_service_notes',
        'admin_activity_notes',
        'accommodation',
        'transport',
        'extra_services',
        'activities',
    ];

    public static get(id) {
        return axios.get('/budget/' + id);
    }

    public static getPdfDetails(id) {
        return axios.get('/budget/pdf/details/' + id);
    }

    // P-07: pide una URL de descarga FIRMADA y temporal (sin el token del admin
    // dentro). El componente navega a ella. Antes se pegaba el JWT en la URL,
    // que quedaba en el historial del navegador y en los logs de acceso.
    public static downloadUrl(id) {
        return axios.get('/budget/download-url/' + id);
    }

    public static list(pagination, filters = {}) {
        const params = ApiHelper.setQueryParams(pagination, filters);
        return axios.get('/budget', { params: params });
    }

    public static update(budget) {
		budget.activities = this.prepareActivities(budget);
		// N-234: la cantidad de cada servicio extra viaja DENTRO de su objeto, hermana
		// de la tarifa elegida, que es de donde la lee el backend
		// (`setBudgetExtraServiceByAdmin`). Se envía una COPIA y no se toca el array
		// original: ese array es el v-model de las casillas del selector y su forma es
		// la identidad de cada una — escribirle una clave encima las dejaría
		// DESMARCADAS al reabrir la ficha (M-13).
		const payload = Object.assign({}, budget, {
			extra_service: this.withQuantities(budget.extra_service, budget.extra_service_quantity),
		});
		const data = ApiHelper.buildParamsWithFile(this.fields, payload);
        return axios.post('/budget/' + budget.id, data);
    }

    public static updatePDF(budget) {
        const data = ApiHelper.buildParams(this.pdf_fields, budget);
        return axios.put('/budget/pdf/' + budget.id, data);
    }

	public static resendEmail(id) {
		return axios.get(`/budget/resend-email/${id}`);
	}
	public static externalSync(id) {
		return axios.get(`/budget/external-sync/${id}`);
	}

	/**
	 * N-234 · Cada servicio extra elegido, en un objeto NUEVO y con sus unidades.
	 *
	 * El campo se manda SIEMPRE, con su valor por defecto explícito: en el backend los
	 * tres escritores de extras arman su petición con `Request()` —que devuelve la
	 * petición en curso, la misma en cada vuelta del bucle— y `merge()` acumula, así que
	 * un campo mezclado solo a veces se le queda pegado al servicio siguiente (medido:
	 * un servicio que pedía una unidad se guardó con las 3 del anterior).
	 *
	 * Lo que no es un array se devuelve tal cual, para que un presupuesto sin extras siga
	 * sin mandar el campo: mandarlo vacío haría que el backend BORRARA las líneas.
	 */
	private static withQuantities(extraServices, quantities) {
		if (!Array.isArray(extraServices)) {
			return extraServices;
		}
		const unidades = {};
		for (const pareja of quantities || []) {
			unidades[pareja.extra_service_id] = pareja.quantity;
		}
		return extraServices.map((servicio) => Object.assign({}, servicio, {
			quantity: unidades[servicio.extra_service_id] !== undefined
				? unidades[servicio.extra_service_id]
				: 1,
		}));
	}

	/**
	 * N-264 · Las tarifas de una media jornada, SIEMPRE como ids.
	 *
	 * Es la forma que espera el servidor (`BudgetActivitiesService::setPriceBudgetActivity`
	 * las busca por id), pero `GET /api/budget/{id}` las sirve como objetos
	 * `{id, name, price, group}`, y solo el selector del día las convierte al montarse. Si
	 * se guarda antes —sin haber llegado el catálogo de actividades— viajaban como objetos,
	 * el servidor no encontraba ninguna tarifa obligatoria y BORRABA la actividad en
	 * silencio, con un 200.
	 */
	private static tariffIds(prices) {
		return (prices || []).map((price) => (price !== null && typeof price === 'object' ? price.id : price));
	}

	private static prepareActivities(budget) {
		const res: any[] = [];
		for (const day of budget.activities) {
			for (const part of ['day_part_1', 'day_part_2', 'day_part_3']) {
				if (day[part] && day[part].activity_id) {
					res.push(Object.assign({}, day[part], { prices: this.tariffIds(day[part].prices) }));
				}
			}
		}
		return res;
	}

}
